import Aura from '@primeuix/themes/aura';
import { PaletteDesignToken } from '@primeuix/themes/types';

import { BRAND_PALETTE } from '../../theme/constant/theme.constant';
import { IPrimaryColor } from '../interfaces/layout.interface';

const aura = Aura.primitive;

// Black buttons in light mode and near-white ones in dark, on slate: the theme's buttons are
// 600, 700 and 800 in light mode and 400, 300 and 200 in dark.
const NOIR_PALETTE: PaletteDesignToken = {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#f1f5f9',
    400: '#f8fafc',
    500: '#64748b',
    600: '#020617',
    700: '#1e293b',
    800: '#334155',
    900: '#0f172a',
    950: '#020617',
};

export const BRAND_PRIMARY = 'brand';

// A swatch shows the button's own colour in each mode: the theme's buttons are 600 in light
// mode and 400 in dark.
const toPrimaryColor = (name: string, palette: PaletteDesignToken | undefined): IPrimaryColor => ({
    name,
    value: `light-dark(${palette?.[600]}, ${palette?.[400]})`,
    palette,
});

export const primaryColors: IPrimaryColor[] = [
    // AI-ARCS's logo blue, the default.
    toPrimaryColor(BRAND_PRIMARY, BRAND_PALETTE),
    toPrimaryColor('emerald', aura?.emerald),
    toPrimaryColor('noir', NOIR_PALETTE),
    toPrimaryColor('green', aura?.green),
    toPrimaryColor('lime', aura?.lime),
    toPrimaryColor('orange', aura?.orange),
    toPrimaryColor('amber', aura?.amber),
    toPrimaryColor('yellow', aura?.yellow),
    toPrimaryColor('teal', aura?.teal),
    toPrimaryColor('cyan', aura?.cyan),
    toPrimaryColor('sky', aura?.sky),
    toPrimaryColor('blue', aura?.blue),
    toPrimaryColor('indigo', aura?.indigo),
    toPrimaryColor('violet', aura?.violet),
    toPrimaryColor('purple', aura?.purple),
    toPrimaryColor('fuchsia', aura?.fuchsia),
    toPrimaryColor('pink', aura?.pink),
    toPrimaryColor('rose', aura?.rose),
    toPrimaryColor('red', aura?.red),
];

export const surfaceColors = [
    { name: 'slate', value: '#64748b' },
    { name: 'gray', value: '#6b7280' },
    { name: 'zinc', value: '#71717a' },
    { name: 'neutral', value: '#737373' },
    { name: 'stone', value: '#78716c' },
    { name: 'soho', value: '#4b5563' },
    { name: 'viva', value: '#3f3f46' },
    { name: 'ocean', value: '#334155' },
];

export const surfacePalettes: Record<string, Record<string, string>> = {
    slate: {
        '0': '#ffffff',
        '50': '#f8fafc',
        '100': '#f1f5f9',
        '200': '#e2e8f0',
        '300': '#cbd5e1',
        '400': '#94a3b8',
        '500': '#64748b',
        '600': '#475569',
        '700': '#334155',
        '800': '#1e293b',
        '900': '#0f172a',
        '950': '#020617',
    },
    gray: {
        '0': '#ffffff',
        '50': '#f9fafb',
        '100': '#f3f4f6',
        '200': '#e5e7eb',
        '300': '#d1d5db',
        '400': '#9ca3af',
        '500': '#6b7280',
        '600': '#4b5563',
        '700': '#374151',
        '800': '#1f2937',
        '900': '#111827',
        '950': '#030712',
    },
    zinc: {
        '0': '#ffffff',
        '50': '#fafafa',
        '100': '#f4f4f5',
        '200': '#e4e4e7',
        '300': '#d4d4d8',
        '400': '#a1a1aa',
        '500': '#71717a',
        '600': '#52525b',
        '700': '#3f3f46',
        '800': '#27272a',
        '900': '#18181b',
        '950': '#09090b',
    },
    neutral: {
        '0': '#ffffff',
        '50': '#fafafa',
        '100': '#f5f5f5',
        '200': '#e5e5e5',
        '300': '#d4d4d4',
        '400': '#a3a3a3',
        '500': '#737373',
        '600': '#525252',
        '700': '#404040',
        '800': '#262626',
        '900': '#171717',
        '950': '#0a0a0a',
    },
    stone: {
        '0': '#ffffff',
        '50': '#fafaf9',
        '100': '#f5f5f4',
        '200': '#e7e5e4',
        '300': '#d6d3d1',
        '400': '#a8a29e',
        '500': '#78716c',
        '600': '#57534e',
        '700': '#44403c',
        '800': '#292524',
        '900': '#1c1917',
        '950': '#0c0a09',
    },
    soho: {
        '0': '#ffffff',
        '50': '#f9fafb',
        '100': '#f3f4f6',
        '200': '#e5e7eb',
        '300': '#d1d5db',
        '400': '#9ca3af',
        '500': '#6b7280',
        '600': '#4b5563',
        '700': '#374151',
        '800': '#1f2937',
        '900': '#111827',
        '950': '#030712',
    },
    viva: {
        '0': '#ffffff',
        '50': '#fafafa',
        '100': '#f4f4f5',
        '200': '#e4e4e7',
        '300': '#d4d4d8',
        '400': '#a1a1aa',
        '500': '#71717a',
        '600': '#52525b',
        '700': '#3f3f46',
        '800': '#27272a',
        '900': '#18181b',
        '950': '#09090b',
    },
    ocean: {
        '0': '#ffffff',
        '50': '#f8fafc',
        '100': '#f1f5f9',
        '200': '#e2e8f0',
        '300': '#cbd5e1',
        '400': '#94a3b8',
        '500': '#64748b',
        '600': '#475569',
        '700': '#334155',
        '800': '#1e293b',
        '900': '#0f172a',
        '950': '#020617',
    },
};
